/**
 * Material Manager
 * 
 * Manages PBR material customization - swapping textures on specific
 * mesh slots and tracking the current material state.
 */

import * as THREE from 'three';
import { TextureLoader, type LoadedPBRTextures } from '../loaders';
import { MaterialSlotPatternConfig } from './MaterialSlotPatternConfig';
import type { 
  IManager, 
  MaterialSlot, 
  MaterialProps
} from '../types';

export interface MaterialState {
  slotId: string;
  selectedOptionId: string;
}

export class MaterialManager implements IManager {
  private scene: THREE.Scene | null = null;
  private textureLoader: TextureLoader;
  private slots: MaterialSlot[] = [];
  private state = new Map<string, string>(); // slotId -> optionId
  private meshMaterialMap = new Map<string, THREE.MeshStandardMaterial[]>();
  private originalMaterials = new Map<string, THREE.Material>();
  
  // Pattern-based material slots detected from model
  private patternConfigs = new Map<string, MaterialSlotPatternConfig>();
  
  private onChangeCallback?: (slotId: string, optionId: string) => void;
  
  constructor(textureLoader?: TextureLoader) {
    this.textureLoader = textureLoader ?? new TextureLoader();
  }
  
  /**
   * Initialize with scene and material slot configuration
   */
  public async init(scene: THREE.Scene, config: { slots: MaterialSlot[] }): Promise<void> {
    this.scene = scene;
    this.slots = config.slots;
    
    // Auto-detect material slots from the scene
    this.autoDetectMaterialSlots();
    
    // Build mesh-to-material mapping
    this.buildMeshMaterialMap();
    
    // Apply default selections
    for (const slot of this.slots) {
      const defaultId = slot.defaultOptionId ?? slot.options[0]?.id;
      if (defaultId) {
        await this.selectOption(slot.id, defaultId);
      }
    }
  }
  
  /**
   * Auto-detect material slots from the loaded scene, organized by object
   */
  private autoDetectMaterialSlots(): void {
    if (!this.scene) return;
    
    // Track materials with their parent object info
    interface MaterialInfo {
      material: THREE.Material;
      meshName: string;
      objectName: string;
      materialIndex?: number;
    }
    
    const materialList: MaterialInfo[] = [];
    
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        const meshName = object.name || 'unnamed';
        // Find the parent object name (typically the node that contains the mesh)
        const objectName = this.findParentObjectName(object) || meshName;
        
        if (Array.isArray(object.material)) {
          // Multi-material mesh - each material gets its own slot
          object.material.forEach((mat, index) => {
            materialList.push({
              material: mat,
              meshName,
              objectName,
              materialIndex: index,
            });
          });
        } else {
          // Single material
          materialList.push({
            material: object.material,
            meshName,
            objectName,
          });
        }
      }
    });
    
    // Create MaterialSlotPatternConfig for each material, keyed by material UUID + object
    let slotIndex = 0;
    const processedMaterials = new Set<string>();
    
    for (const { material, meshName, objectName, materialIndex } of materialList) {
      // Create unique key per material instance (same material can be on multiple objects)
      const uniqueKey = materialIndex !== undefined 
        ? `${material.uuid}_${materialIndex}_${objectName}`
        : `${material.uuid}_${objectName}`;
      
      if (processedMaterials.has(uniqueKey)) continue;
      processedMaterials.add(uniqueKey);
      
      // Try to find a configured slot that matches this mesh name
      const matchingConfiguredSlot = this.slots.find((configured) =>
        this.meshMatchesSlot(meshName, configured.meshNames)
      );

      const materialName = matchingConfiguredSlot?.name ?? material.name ?? `Material ${slotIndex + 1}`;
      const slotId = matchingConfiguredSlot?.id ?? `slot_${slotIndex}`;

      const patternConfig = new MaterialSlotPatternConfig({
        id: slotId,
        name: materialName,
        originalMaterial: material,
        materialIndex: materialIndex,
        objectName: objectName,
      });

      // Attach detected mesh name
      patternConfig.addMeshName(meshName);

      // If we matched a configured slot, copy its options and default selection
      if (matchingConfiguredSlot) {
        // Map configured TextureOption -> MaterialSlotOption shape
        patternConfig.options = matchingConfiguredSlot.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          materialProps: opt.materialProps,
          textureUrls: {
            map: opt.textures.map,
            normalMap: opt.textures.normalMap,
            roughnessMap: opt.textures.roughnessMap,
            metalnessMap: opt.textures.metalnessMap,
            aoMap: opt.textures.aoMap,
            emissiveMap: opt.textures.emissiveMap,
          },
        }));

        patternConfig.selectedOptionId = matchingConfiguredSlot.defaultOptionId ?? matchingConfiguredSlot.options[0]?.id ?? null;
      } else {
        // Generate default color options for auto-detected materials without config
        this.addDefaultColorOptions(patternConfig, material);
      }

      this.patternConfigs.set(slotId, patternConfig);
      slotIndex++;
    }
    
    // Enhanced logging for debugging - grouped by object
    console.log('[MaterialManager] ═══════════════════════════════════════');
    console.log('[MaterialManager] Material Slot Auto-Detection Summary:');
    console.log(`[MaterialManager]   Total materials detected: ${this.patternConfigs.size}`);
    console.log('[MaterialManager] ═══════════════════════════════════════');
    
    // Group by object for cleaner logging
    const byObject = new Map<string, MaterialSlotPatternConfig[]>();
    Array.from(this.patternConfigs.values()).forEach((cfg) => {
      const objName = cfg.objectName || 'Unknown';
      if (!byObject.has(objName)) {
        byObject.set(objName, []);
      }
      byObject.get(objName)!.push(cfg);
    });
    
    for (const [objName, configs] of byObject) {
      console.log(`[MaterialManager] 📦 Object: "${objName}"`);
      for (const cfg of configs) {
        const mat = cfg.originalMaterial as THREE.MeshStandardMaterial;
        const colorHex = mat.color ? `#${mat.color.getHexString()}` : 'n/a';
        console.log(`[MaterialManager]   └─ Material: "${cfg.name}" (${cfg.id})`);
        console.log(`[MaterialManager]      Color: ${colorHex}, Options: ${cfg.options.length}`);
      }
    }
  }
  
  /**
   * Find the parent object name for a mesh.
   * Uses the immediate parent group name, or the mesh's own name.
   * Preserves exact names from the 3D model without modification.
   */
  private findParentObjectName(mesh: THREE.Mesh): string | null {
    // First check if the mesh has a direct named parent (Group)
    if (mesh.parent && mesh.parent.type === 'Group' && mesh.parent.name && mesh.parent.name !== 'Scene') {
      return mesh.parent.name;
    }
    
    // Otherwise use the mesh's own name as-is
    if (mesh.name) {
      return mesh.name;
    }
    
    return null;
  }
  
  /**
   * Add default color options for auto-detected materials
   */
  private addDefaultColorOptions(config: MaterialSlotPatternConfig, material: THREE.Material): void {
    const mat = material as THREE.MeshStandardMaterial;
    
    // Default color palette options
    const colorOptions = [
      { id: 'white', name: 'White', color: '#ffffff' },
      { id: 'black', name: 'Black', color: '#1a1a1a' },
      { id: 'red', name: 'Red', color: '#cc2233' },
      { id: 'blue', name: 'Blue', color: '#2255cc' },
      { id: 'green', name: 'Green', color: '#22aa44' },
      { id: 'gold', name: 'Gold', color: '#d4af37', metalness: 0.7, roughness: 0.3 },
      { id: 'silver', name: 'Silver', color: '#c0c0c0', metalness: 0.8, roughness: 0.2 },
      { id: 'wood', name: 'Wood', color: '#8B4513', roughness: 0.8, metalness: 0.0 },
    ];
    
    // Keep the "Original" option that was added in constructor, then add color options
    for (const opt of colorOptions) {
      config.addOption({
        id: opt.id,
        name: opt.name,
        materialProps: {
          color: opt.color,
          roughness: opt.roughness ?? mat.roughness,
          metalness: opt.metalness ?? mat.metalness,
        },
      });
    }
  }
  
  /**
   * Build mapping of mesh names to their materials
   */
  private buildMeshMaterialMap(): void {
    if (!this.scene) return;
    
    this.meshMaterialMap.clear();
    this.originalMaterials.clear();
    
    const meshesFound: string[] = [];
    
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        const meshName = object.name || 'unnamed';
        meshesFound.push(meshName);
        
        // Store original materials
        if (Array.isArray(object.material)) {
          object.material.forEach((mat, i) => {
            this.originalMaterials.set(`${meshName}_${i}`, mat.clone());
          });
        } else {
          this.originalMaterials.set(meshName, object.material.clone());
        }
        
        // Check which slots this mesh belongs to
        for (const slot of this.slots) {
          if (this.meshMatchesSlot(meshName, slot.meshNames)) {
            if (!this.meshMaterialMap.has(slot.id)) {
              this.meshMaterialMap.set(slot.id, []);
            }
            
            // Handle multi-material meshes
            if (Array.isArray(object.material)) {
              for (let i = 0; i < object.material.length; i++) {
                const mat = this.ensureStandardMaterialFromArray(object, i);
                this.meshMaterialMap.get(slot.id)!.push(mat);
              }
            } else {
              const material = this.ensureStandardMaterial(object);
              this.meshMaterialMap.get(slot.id)!.push(material);
            }
          }
        }
      }
    });
    
    console.log('[MaterialManager] Meshes found:', meshesFound);
    console.log('[MaterialManager] Material map:', Array.from(this.meshMaterialMap.entries()).map(([k, v]) => `${k}: ${v.length} materials`));
  }
  
  /**
   * Check if a mesh name matches any of the slot's mesh patterns
   */
  private meshMatchesSlot(meshName: string, patterns: string[]): boolean {
    for (const pattern of patterns) {
      if (pattern.includes('*')) {
        // Wildcard matching
        const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
        if (regex.test(meshName)) return true;
      } else {
        // Exact match
        if (meshName === pattern) return true;
      }
    }
    return false;
  }
  
  /**
   * Ensure mesh has a MeshStandardMaterial (convert if needed)
   */
  private ensureStandardMaterial(mesh: THREE.Mesh): THREE.MeshStandardMaterial {
    if (mesh.material instanceof THREE.MeshStandardMaterial) {
      return mesh.material;
    }
    
    // Convert to MeshStandardMaterial
    const oldMat = mesh.material as THREE.Material;
    const newMat = new THREE.MeshStandardMaterial();
    
    // Copy basic properties if available
    if ('color' in oldMat) {
      newMat.color = (oldMat as THREE.MeshBasicMaterial).color.clone();
    }
    if ('map' in oldMat) {
      newMat.map = (oldMat as THREE.MeshBasicMaterial).map;
    }
    
    mesh.material = newMat;
    return newMat;
  }
  
  /**
   * Ensure material at index in multi-material mesh is MeshStandardMaterial
   */
  private ensureStandardMaterialFromArray(mesh: THREE.Mesh, index: number): THREE.MeshStandardMaterial {
    const materials = mesh.material as THREE.Material[];
    const mat = materials[index];
    
    if (mat instanceof THREE.MeshStandardMaterial) {
      return mat;
    }
    
    // Convert to MeshStandardMaterial
    const newMat = new THREE.MeshStandardMaterial();
    
    if ('color' in mat) {
      newMat.color = (mat as THREE.MeshBasicMaterial).color.clone();
    }
    if ('map' in mat) {
      newMat.map = (mat as THREE.MeshBasicMaterial).map;
    }
    
    materials[index] = newMat;
    return newMat;
  }
  
  /**
   * Select a texture option for a material slot (supports both configured slots and auto-detected pattern configs)
   */
  public async selectOption(slotId: string, optionId: string): Promise<void> {
    // First try configured slots
    const slot = this.slots.find((s) => s.id === slotId);
    
    if (slot) {
      // Handle configured slot
      const option = slot.options.find((o) => o.id === optionId);
      if (!option) {
        console.warn(`Material option not found: ${optionId} in slot ${slotId}`);
        return;
      }
      
      // Load textures
      const textures = await this.textureLoader.loadPBRTextures(option.textures);
      
      // Apply to all materials in this slot
      const materials = this.meshMaterialMap.get(slotId);
      if (materials) {
        for (const material of materials) {
          this.applyTexturesToMaterial(material, textures, option.materialProps);
        }
      }
      
      // Update state
      this.state.set(slotId, optionId);
      this.onChangeCallback?.(slotId, optionId);
      return;
    }
    
    // Fall back to pattern configs (auto-detected)
    const patternConfig = this.patternConfigs.get(slotId);
    if (patternConfig) {
      const option = patternConfig.options.find((o) => o.id === optionId);
      if (!option) {
        console.warn(`Pattern option not found: ${optionId} in slot ${slotId}`);
        return;
      }
      
      console.log(`[MaterialManager] Applying pattern option "${option.name}" to slot "${slotId}"`);
      
      // Get the original material and apply changes directly
      const originalMat = patternConfig.originalMaterial as THREE.MeshStandardMaterial;
      
      // Apply material props
      if (option.materialProps) {
        if (option.materialProps.color) originalMat.color.set(option.materialProps.color);
        if (option.materialProps.roughness !== undefined) originalMat.roughness = option.materialProps.roughness;
        if (option.materialProps.metalness !== undefined) originalMat.metalness = option.materialProps.metalness;
        if (option.materialProps.emissive) originalMat.emissive.set(option.materialProps.emissive);
        if (option.materialProps.emissiveIntensity !== undefined) {
          originalMat.emissiveIntensity = option.materialProps.emissiveIntensity;
        }
        if (option.materialProps.opacity !== undefined) originalMat.opacity = option.materialProps.opacity;
        if (option.materialProps.transparent !== undefined) originalMat.transparent = option.materialProps.transparent;
      }
      
      // Load and apply textures if specified
      if (option.textureUrls) {
        const textures = await this.textureLoader.loadPBRTextures({
          map: option.textureUrls.map,
          normalMap: option.textureUrls.normalMap,
          roughnessMap: option.textureUrls.roughnessMap,
          metalnessMap: option.textureUrls.metalnessMap,
          aoMap: option.textureUrls.aoMap,
          emissiveMap: option.textureUrls.emissiveMap,
        });
        
        if (textures.map) originalMat.map = textures.map;
        if (textures.normalMap) originalMat.normalMap = textures.normalMap;
        if (textures.roughnessMap) originalMat.roughnessMap = textures.roughnessMap;
        if (textures.metalnessMap) originalMat.metalnessMap = textures.metalnessMap;
        if (textures.aoMap) originalMat.aoMap = textures.aoMap;
        if (textures.emissiveMap) originalMat.emissiveMap = textures.emissiveMap;
      }
      
      originalMat.needsUpdate = true;
      
      // Update pattern config state
      patternConfig.selectOption(optionId);
      this.state.set(slotId, optionId);
      this.onChangeCallback?.(slotId, optionId);
      return;
    }
    
    console.warn(`Material slot not found: ${slotId}`);
  }
  
  /**
   * Apply loaded textures to a material
   */
  private applyTexturesToMaterial(
    material: THREE.MeshStandardMaterial,
    textures: LoadedPBRTextures,
    props?: MaterialProps
  ): void {
    // Apply textures
    if (textures.map) material.map = textures.map;
    if (textures.normalMap) material.normalMap = textures.normalMap;
    if (textures.roughnessMap) material.roughnessMap = textures.roughnessMap;
    if (textures.metalnessMap) material.metalnessMap = textures.metalnessMap;
    if (textures.aoMap) material.aoMap = textures.aoMap;
    if (textures.emissiveMap) material.emissiveMap = textures.emissiveMap;
    
    // Apply material property overrides
    if (props) {
      if (props.color) material.color.set(props.color);
      if (props.roughness !== undefined) material.roughness = props.roughness;
      if (props.metalness !== undefined) material.metalness = props.metalness;
      if (props.emissive) material.emissive.set(props.emissive);
      if (props.emissiveIntensity !== undefined) {
        material.emissiveIntensity = props.emissiveIntensity;
      }
      if (props.opacity !== undefined) material.opacity = props.opacity;
      if (props.transparent !== undefined) material.transparent = props.transparent;
    }
    
    material.needsUpdate = true;
  }
  
  /**
   * Get all available slots
   */
  public getSlots(): MaterialSlot[] {
    return this.slots;
  }
  
  /**
   * Get auto-detected pattern configs
   */
  public getPatternConfigs(): MaterialSlotPatternConfig[] {
    return Array.from(this.patternConfigs.values());
  }
  
  /**
   * Get current state
   */
  public getState(): MaterialState[] {
    return Array.from(this.state.entries()).map(([slotId, selectedOptionId]) => ({
      slotId,
      selectedOptionId,
    }));
  }
  
  /**
   * Get selected option for a slot
   */
  public getSelectedOption(slotId: string): string | undefined {
    return this.state.get(slotId);
  }
  
  /**
   * Set change callback
   */
  public onChange(callback: (slotId: string, optionId: string) => void): void {
    this.onChangeCallback = callback;
  }
  
  /**
   * Reset to default materials
   */
  public resetToDefaults(): void {
    for (const slot of this.slots) {
      const defaultId = slot.defaultOptionId ?? slot.options[0]?.id;
      if (defaultId) {
        this.selectOption(slot.id, defaultId);
      }
    }
  }
  
  /**
   * Update (called each frame) - no-op for materials
   */
  public update(_deltaTime: number): void {
    // Materials don't need per-frame updates
  }
  
  /**
   * Clean up resources
   */
  public dispose(): void {
    // Restore original materials
    if (this.scene) {
      this.scene.traverse((object) => {
        if (object instanceof THREE.Mesh && object.name) {
          const original = this.originalMaterials.get(object.name);
          if (original) {
            object.material = original;
          }
        }
      });
    }
    
    this.textureLoader.dispose();
    this.meshMaterialMap.clear();
    this.originalMaterials.clear();
    this.state.clear();
    this.scene = null;
  }
}
